import { supabase } from '../lib/supabase';
export const teamService = {
    // =====================================================
    // TEAM CRUD OPERATIONS
    // =====================================================
    /**
     * Fetch all teams for an organization
     */
    async fetchTeams(organizationId) {
        try {
            const { data, error } = await supabase
                .from('teams')
                .select(`
          *,
          members:team_members(count)
        `)
                .eq('organization_id', organizationId)
                .order('name', { ascending: true });
            if (error) {
                console.error('Error fetching teams:', error);
                throw error;
            }
            // Fetch detailed members for each team
            const teamsWithMembers = await Promise.all((data || []).map(async (team) => {
                const members = await this.fetchTeamMembers(team.id);
                return {
                    ...team,
                    memberCount: team.members[0]?.count || 0,
                    members
                };
            }));
            return teamsWithMembers;
        }
        catch (error) {
            console.error('Failed to fetch teams:', error);
            return [];
        }
    },
    /**
     * Fetch teams where user is a member
     */
    async fetchUserTeams(userId) {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .select(`
          team_id,
          teams (
            id,
            organization_id,
            name,
            description,
            icon,
            color,
            is_private,
            has_description,
            has_work,
            has_members,
            created_by,
            created_at,
            updated_at
          )
        `)
                .eq('user_id', userId);
            if (error) {
                console.error('Error fetching user teams:', error);
                throw error;
            }
            // Transform the response and fetch members for each team
            const teams = data
                ?.map(item => item.teams)
                .filter(team => team !== null) || [];
            // Fetch detailed members for each team
            const teamsWithMembers = await Promise.all(teams.map(async (team) => {
                const members = await this.fetchTeamMembers(team.id);
                return {
                    ...team,
                    isPrivate: team.is_private,
                    hasDescription: team.has_description,
                    hasWork: team.has_work,
                    hasMembers: team.has_members,
                    createdBy: team.created_by,
                    createdAt: team.created_at,
                    updatedAt: team.updated_at,
                    organizationId: team.organization_id,
                    memberCount: members.length,
                    members
                };
            }));
            return teamsWithMembers;
        }
        catch (error) {
            console.error('Failed to fetch user teams:', error);
            return [];
        }
    },
    /**
     * Fetch a single team by ID with full details
     */
    async fetchTeam(teamId) {
        try {
            const { data, error } = await supabase
                .from('teams')
                .select('*')
                .eq('id', teamId)
                .single();
            if (error) {
                console.error('Error fetching team:', error);
                throw error;
            }
            if (!data)
                return null;
            // Fetch members separately
            const members = await this.fetchTeamMembers(teamId);
            return {
                id: data.id,
                organizationId: data.organization_id,
                name: data.name,
                description: data.description,
                icon: data.icon,
                color: data.color,
                isPrivate: data.is_private,
                hasDescription: data.has_description,
                hasWork: data.has_work,
                hasMembers: data.has_members,
                createdBy: data.created_by,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
                memberCount: members.length,
                members
            };
        }
        catch (error) {
            console.error('Failed to fetch team:', error);
            return null;
        }
    },
    /**
     * Create a new team
     */
    async createTeam(teamData) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('teams')
                .insert({
                organization_id: teamData.organizationId,
                name: teamData.name,
                description: teamData.description || null,
                icon: teamData.icon || 'S',
                color: teamData.color || '#6366F1',
                is_private: teamData.isPrivate || false,
                created_by: user.id
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating team:', error);
                throw error;
            }
            return this.fetchTeam(data.id);
        }
        catch (error) {
            console.error('Failed to create team:', error);
            return null;
        }
    },
    /**
     * Update an existing team
     */
    async updateTeam(teamId, updates) {
        try {
            const updateData = {};
            if (updates.name !== undefined)
                updateData.name = updates.name;
            if (updates.description !== undefined)
                updateData.description = updates.description;
            if (updates.icon !== undefined)
                updateData.icon = updates.icon;
            if (updates.color !== undefined)
                updateData.color = updates.color;
            if (updates.isPrivate !== undefined)
                updateData.is_private = updates.isPrivate;
            console.log('🔵 teamService.updateTeam: Updating team', teamId, 'with:', updateData);
            const { error } = await supabase
                .from('teams')
                .update(updateData)
                .eq('id', teamId);
            if (error) {
                console.error('🔴 teamService.updateTeam: Error updating team:', error);
                throw error;
            }
            console.log('✅ teamService.updateTeam: Team updated successfully');
            return true;
        }
        catch (error) {
            console.error('🔴 teamService.updateTeam: Failed to update team:', error);
            return false;
        }
    },
    /**
     * Delete a team
     */
    async deleteTeam(teamId) {
        try {
            const { error } = await supabase
                .from('teams')
                .delete()
                .eq('id', teamId);
            if (error) {
                console.error('Error deleting team:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete team:', error);
            return false;
        }
    },
    // =====================================================
    // TEAM MEMBER OPERATIONS
    // =====================================================
    /**
     * Fetch all members of a team
     */
    async fetchTeamMembers(teamId) {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .select('*')
                .eq('team_id', teamId)
                .order('added_at', { ascending: false });
            if (error) {
                console.error('Error fetching team members:', error);
                throw error;
            }
            // Fetch user/agent details separately if needed
            const membersWithDetails = await Promise.all((data || []).map(async (member) => {
                if (member.member_type === 'user' && member.user_id) {
                    const { data: userData } = await supabase
                        .from('profiles')
                        .select('id, full_name, email, avatar_url')
                        .eq('id', member.user_id)
                        .single();
                    return {
                        ...member,
                        name: userData?.full_name || 'Unknown',
                        email: userData?.email,
                        avatar: userData?.avatar_url
                    };
                }
                return {
                    ...member,
                    name: member.agent_id || 'Unknown Agent'
                };
            }));
            return membersWithDetails.map(member => ({
                id: member.id,
                teamId: member.team_id,
                memberType: member.member_type,
                userId: member.user_id,
                agentId: member.agent_id,
                name: member.name || 'Unknown',
                email: member.email,
                avatar: member.avatar,
                role: member.role,
                addedBy: member.added_by,
                addedAt: member.added_at
            }));
        }
        catch (error) {
            console.error('Failed to fetch team members:', error);
            return [];
        }
    },
    /**
     * Add a member to a team (user or AI agent)
     */
    async addTeamMember(memberData) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            // Validate UUID format for agent_id if present
            if (memberData.agentId && !this.isValidUUID(memberData.agentId)) {
                console.warn(`Invalid agent ID format: ${memberData.agentId}. Skipping agent addition.`);
                return false;
            }
            const { error } = await supabase
                .from('team_members')
                .insert({
                team_id: memberData.teamId,
                member_type: memberData.memberType,
                user_id: memberData.userId || null,
                agent_id: memberData.agentId || null,
                role: memberData.role,
                added_by: user.id
            });
            if (error) {
                console.error('Error adding team member:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to add team member:', error);
            return false;
        }
    },
    /**
     * Validate UUID format
     */
    isValidUUID(uuid) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    },
    /**
     * Update a team member's role
     */
    async updateTeamMember(memberId, updates) {
        try {
            const { error } = await supabase
                .from('team_members')
                .update({ role: updates.role })
                .eq('id', memberId);
            if (error) {
                console.error('Error updating team member:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to update team member:', error);
            return false;
        }
    },
    /**
     * Remove a member from a team
     */
    async removeTeamMember(memberId) {
        try {
            const { error } = await supabase
                .from('team_members')
                .delete()
                .eq('id', memberId);
            if (error) {
                console.error('Error removing team member:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to remove team member:', error);
            return false;
        }
    },
    // =====================================================
    // TEAM PERMISSIONS OPERATIONS
    // =====================================================
    /**
     * Fetch permissions for a team role
     */
    async fetchTeamPermissions(teamId, role) {
        try {
            const { data, error } = await supabase
                .from('team_permissions')
                .select('*')
                .eq('team_id', teamId)
                .eq('role', role)
                .single();
            if (error) {
                console.error('Error fetching team permissions:', error);
                throw error;
            }
            if (!data)
                return null;
            return {
                id: data.id,
                teamId: data.team_id,
                role: data.role,
                canViewTeam: data.can_view_team,
                canCreateWork: data.can_create_work,
                canEditWork: data.can_edit_work,
                canDeleteWork: data.can_delete_work,
                canAddMembers: data.can_add_members,
                canRemoveMembers: data.can_remove_members,
                canEditTeam: data.can_edit_team,
                canDeleteTeam: data.can_delete_team,
                createdAt: data.created_at,
                updatedAt: data.updated_at
            };
        }
        catch (error) {
            console.error('Failed to fetch team permissions:', error);
            return null;
        }
    },
    // =====================================================
    // TEAM GOALS OPERATIONS
    // =====================================================
    /**
     * Fetch all goals for a team
     */
    async fetchTeamGoals(teamId) {
        try {
            const { data, error } = await supabase
                .from('team_goals')
                .select('*')
                .eq('team_id', teamId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching team goals:', error);
                throw error;
            }
            return (data || []).map(goal => ({
                id: goal.id,
                teamId: goal.team_id,
                name: goal.name,
                description: goal.description,
                status: goal.status,
                progress: goal.progress,
                startDate: goal.start_date,
                dueDate: goal.due_date,
                quarter: goal.quarter,
                ownerId: goal.owner_id,
                createdBy: goal.created_by,
                createdAt: goal.created_at,
                updatedAt: goal.updated_at
            }));
        }
        catch (error) {
            console.error('Failed to fetch team goals:', error);
            return [];
        }
    },
    /**
     * Create a new team goal
     */
    async createTeamGoal(goalData) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('team_goals')
                .insert({
                team_id: goalData.teamId,
                name: goalData.name,
                description: goalData.description || null,
                status: goalData.status || 'on_track',
                progress: goalData.progress || 0,
                start_date: goalData.startDate || null,
                due_date: goalData.dueDate || null,
                quarter: goalData.quarter || null,
                owner_id: goalData.ownerId || null,
                created_by: user.id
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating team goal:', error);
                throw error;
            }
            return {
                id: data.id,
                teamId: data.team_id,
                name: data.name,
                description: data.description,
                status: data.status,
                progress: data.progress,
                startDate: data.start_date,
                dueDate: data.due_date,
                quarter: data.quarter,
                ownerId: data.owner_id,
                createdBy: data.created_by,
                createdAt: data.created_at,
                updatedAt: data.updated_at
            };
        }
        catch (error) {
            console.error('Failed to create team goal:', error);
            return null;
        }
    },
    /**
     * Update a team goal
     */
    async updateTeamGoal(goalId, updates) {
        try {
            const updateData = {};
            if (updates.name !== undefined)
                updateData.name = updates.name;
            if (updates.description !== undefined)
                updateData.description = updates.description;
            if (updates.status !== undefined)
                updateData.status = updates.status;
            if (updates.progress !== undefined)
                updateData.progress = updates.progress;
            if (updates.startDate !== undefined)
                updateData.start_date = updates.startDate;
            if (updates.dueDate !== undefined)
                updateData.due_date = updates.dueDate;
            if (updates.quarter !== undefined)
                updateData.quarter = updates.quarter;
            if (updates.ownerId !== undefined)
                updateData.owner_id = updates.ownerId;
            const { error } = await supabase
                .from('team_goals')
                .update(updateData)
                .eq('id', goalId);
            if (error) {
                console.error('Error updating team goal:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to update team goal:', error);
            return false;
        }
    },
    /**
     * Delete a team goal
     */
    async deleteTeamGoal(goalId) {
        try {
            const { error } = await supabase
                .from('team_goals')
                .delete()
                .eq('id', goalId);
            if (error) {
                console.error('Error deleting team goal:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete team goal:', error);
            return false;
        }
    },
    // =====================================================
    // TEAM WORK OPERATIONS
    // =====================================================
    /**
     * Fetch all work items for a team
     */
    async fetchTeamWork(teamId) {
        try {
            const { data, error } = await supabase
                .from('team_work')
                .select('*')
                .eq('team_id', teamId)
                .order('added_at', { ascending: false });
            if (error) {
                console.error('Error fetching team work:', error);
                throw error;
            }
            return (data || []).map(work => ({
                id: work.id,
                teamId: work.team_id,
                workType: work.work_type,
                workId: work.work_id,
                name: work.name,
                description: work.description,
                icon: work.icon,
                color: work.color,
                addedBy: work.added_by,
                addedAt: work.added_at
            }));
        }
        catch (error) {
            console.error('Failed to fetch team work:', error);
            return [];
        }
    },
    /**
     * Add a work item to a team
     */
    async addTeamWork(workData) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { error } = await supabase
                .from('team_work')
                .insert({
                team_id: workData.teamId,
                work_type: workData.workType,
                work_id: workData.workId,
                name: workData.name,
                description: workData.description || null,
                icon: workData.icon || null,
                color: workData.color || null,
                added_by: user.id
            });
            if (error) {
                console.error('Error adding team work:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to add team work:', error);
            return false;
        }
    },
    /**
     * Remove a work item from a team
     */
    async removeTeamWork(workId) {
        try {
            const { error } = await supabase
                .from('team_work')
                .delete()
                .eq('id', workId);
            if (error) {
                console.error('Error removing team work:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to remove team work:', error);
            return false;
        }
    }
};
